import { useSpring, animated, easings } from '@react-spring/web';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BLOCK_CONFIRMATION_TIME_IN_SECONDS, CHAIN_IDS } from '@/constants/chain';

import { useTransactionStore } from '@/store/transaction';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { XIcon } from '@/ui/Icons/XIcon';

import { getBlockExplorerTransactionURL } from '@/utils/blockExplorers';

const FADE_IN_AND_SCALE_UP = 'animate-[fadeInAndScaleUp_400ms_cubic-bezier(0.4,0,0,1)]';
const FADE_IN_WITH_DELAY =
  'opacity-0 animate-[fadeIn_250ms_cubic-bezier(0.4,0,0,1)_100ms_forwards]';
const RADIAL_PROGRESS_FILTER = '[filter:drop-shadow(0_0_8px_rgba(89,50,243,0.3))]';

const RADIUS = 80;
const CIRCUMFERENCE = RADIUS * 2 * Math.PI;
const BLOCKS_TO_WAIT = 2;

/** Renders same-chain confirmation progress with caller-owned checkout actions. */
export const PendingTransactionView = ({ onDismiss }: { onDismiss: () => void }) => {
  const currentTransaction = useTransactionStore((state) => state.currentTransaction);

  const tx = currentTransaction?.tx;
  const txHash = tx?.hash;
  const chainId = tx?.sellToken.chainId;

  const blockConfirmationTime = chainId
    ? Math.ceil(Math.max(BLOCK_CONFIRMATION_TIME_IN_SECONDS[chainId] * BLOCKS_TO_WAIT, 5))
    : 30;

  // Object-form `useSpring` auto-starts on mount (function form requires
  // `api.start()`). `pause` is the imperative escape hatch for `stopAnimation`.
  const springProps = useSpring({
    from: { x: CIRCUMFERENCE },
    to: { x: 0 },
    config: {
      duration: blockConfirmationTime * 1000,
      easing: easings.linear,
    },
    pause: false,
  });
  const [secondsRemaining, setSecondsRemaining] = useState(blockConfirmationTime);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopAnimation = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    springProps.x.stop();
  }, [springProps.x]);

  // Re-anchor the countdown if `blockConfirmationTime` changes after mount
  // (e.g. tx loads later and `chainId` resolves to a different per-chain budget).
  useEffect(() => {
    setSecondsRemaining(blockConfirmationTime);
  }, [blockConfirmationTime]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSecondsRemaining((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const isSuccessful = currentTransaction?.isSuccessful ?? false;
  const isFailed = currentTransaction?.isFailed ?? false;

  useEffect(() => {
    if (isSuccessful || isFailed) {
      stopAnimation();
    }
  }, [isSuccessful, isFailed, stopAnimation]);

  const handleSeeDetails = () => {
    if (!chainId || !txHash) return;
    window.open(getBlockExplorerTransactionURL(chainId, txHash), '_blank', 'noopener,noreferrer');
  };

  const isSwapType = tx?.transactionType === 'swap' || tx?.transactionType === 'solana-swap';
  const showDetailsButton = !!chainId && isSwapType;

  return (
    <div className="relative flex flex-col justify-between gap-12 size-full">
      <button
        type="button"
        aria-label="Dismiss pending screen"
        onClick={onDismiss}
        className="absolute top-4 right-4 p-2 rounded-full text-token-gray-900 hover:bg-token-gray-200 transition-colors z-10"
      >
        <XIcon className="size-5" />
      </button>

      <div className="pt-20 px-6 flex flex-col justify-center items-center">
        <svg
          className={FADE_IN_AND_SCALE_UP}
          width="200"
          height="200"
          aria-label="transaction progress"
          role="progressbar"
        >
          <circle
            stroke="var(--color-token-gray-300)"
            cx="100"
            cy="100"
            r={RADIUS}
            strokeWidth="3"
            fill="none"
            strokeDasharray={CIRCUMFERENCE}
          />
          <animated.circle
            className={RADIAL_PROGRESS_FILTER}
            cx="100"
            cy="100"
            r={RADIUS}
            strokeWidth="3"
            stroke="var(--color-token-purple-900)"
            fill="none"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={springProps.x}
            transform="rotate(-90 100 100)"
          />
          <text
            x="50%"
            y="40%"
            dominantBaseline="mathematical"
            textAnchor="middle"
            fill="var(--color-token-gray-800)"
            className={fontStyles({ variant: 'BODY_MD' })}
          >
            Time left
          </text>
          <text
            x="50%"
            y="55%"
            dominantBaseline="mathematical"
            textAnchor="middle"
            className={fontStyles({ variant: 'H3' })}
            fill="var(--color-token-gray-1600)"
          >
            00:{String(secondsRemaining).padStart(2, '0')}
          </text>
        </svg>

        <div className={`mt-8 flex flex-col items-center min-h-10 ${FADE_IN_WITH_DELAY}`}>
          <span className={fontStyles({ variant: 'H5' })}>Transaction pending...</span>
          {secondsRemaining === 0 && (
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: ['text-token-gray-900', 'mt-2'],
              })}
            >
              Transaction is taking longer than expected.
            </span>
          )}
        </div>
      </div>
      <div className="px-6 pb-4 desktop:pb-6">
        {showDetailsButton && (
          <button
            type="button"
            onClick={handleSeeDetails}
            className={buttons({
              size: 'large',
              color: 'gray',
              shape: 'pill',
              class: ['w-full', FADE_IN_WITH_DELAY],
            })}
          >
            {chainId === CHAIN_IDS.SOLANA ? 'See on Solscan' : 'See details'}
          </button>
        )}
      </div>
    </div>
  );
};
